/**
 * Inventory List Component - Enhanced with ABC Classification, Retailer, Mall,
 * Overstock Indicators, Stock Evolution Charts & Stock Mix Analysis
 */
import React, { useState, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Pagination,
  Grid,
  Tooltip as MuiTooltip,
  IconButton,
  Button,
  Collapse,
  TextField,
} from '@mui/material';
import {
  ExpandMore,
  ChevronRight,
  FileDownload,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useQuery } from '@tanstack/react-query';
import api from '../../services/api';
import UniversalFilters from '../common/UniversalFilters';
import { InventoryStatus } from '../../types';
import { useCurrencyFormat } from '../../hooks/useCurrencyFormat';
import { exportToExcel } from '../../utils/exportUtils';
import ExportMenu from '../Common/ExportMenu';
import { useFullscreen, FullscreenButton } from '../Common/FullscreenButton';

/* ─── Constants ─── */
const ABC_COLORS: Record<string, string> = {
  'a': '#e8f5e9',
  'b': '#e3f2fd',
  'c': '#fff3e0',
  'd': '#fce4ec',
  'e': '#f5f5f5',
};

const ABC_TEXT_COLORS: Record<string, string> = {
  'a': '#2e7d32',
  'b': '#1565c0',
  'c': '#e65100',
  'd': '#c62828',
  'e': '#616161',
};

const ABC_LABELS: Record<string, string> = {
  'a': 'A - Fast Mover',
  'b': 'B - Medium Mover',
  'c': 'C - Slow Mover',
  'd': 'D - Very Slow Mover',
  'e': 'E - Dead Stock',
};

const LIFECYCLE_COLORS: Record<string, string> = {
  NEW: '#B76E79', GROWTH: '#8B6F8B', MATURE: '#722F37',
  DECLINING: '#C9A96E', PHASE_OUT: '#D4A574', DISCONTINUED: '#9CA3AF',
};

const ABC_CHART_COLORS: Record<string, string> = {
  A: '#722F37', B: '#C9A96E', C: '#8B7355', D: '#9CA3AF', E: '#D1D5DB',
};

const STATUS_COLORS: Record<string, string> = {
  RED: '#DC2626', YELLOW: '#F59E0B', GREEN: '#16A34A',
};

const CATEGORY_COLORS = [
  '#722F37', '#B76E79', '#C9A96E', '#8B6F8B', '#DEB5A0',
  '#8B7355', '#D4A574', '#9CA3AF', '#6B8E9B', '#A0826D',
];

const chartTooltipStyle = {
  background: '#FDFBF7',
  border: '1px solid #E8E0D4',
  borderRadius: '8px',
  boxShadow: '0 4px 20px rgba(114,47,55,0.08)',
  fontSize: '12px',
  padding: '10px 14px',
};

/* ─── Helpers ─── */
const fmtNumber = (v: number) => v.toLocaleString('en-US');
/* ─── Custom pie label ─── */
const renderPieLabel = ({ name, percent }: { name: string; percent: number }) =>
  percent > 0.03 ? `${name} ${(percent * 100).toFixed(0)}%` : '';

/* ─── Component ─── */
const InventoryList: React.FC = () => {
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [page, setPage] = useState<number>(1);
  const itemsPerPage = 50;
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [chartsOpen, setChartsOpen] = useState(true);
  const { isFullscreen, toggle: toggleFullscreen, FullscreenWrap } = useFullscreen();
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const { fmtCurrencyCompact: fmtCurrency, fmtCurrencyLabel } = useCurrencyFormat();

  // Universal filters state
  const [filters, setFilters] = useState<{
    brands: string[];
    stores: string[];
    categories: string[];
    cities: string[];
    abcClassifications: string[];
    start_date: string;
    end_date: string;
  }>({
    brands: [],
    stores: [],
    categories: [],
    cities: [],
    abcClassifications: [],
    start_date: new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0],
  });

  // Helper to build filter params
  const getFilterParams = () => ({
    status_filter: statusFilter || undefined,
    brands: filters.brands.length > 0 ? filters.brands.join(',') : undefined,
    stores: filters.stores.length > 0 ? filters.stores.join(',') : undefined,
    categories: filters.categories.length > 0 ? filters.categories.join(',') : undefined,
    cities: filters.cities.length > 0 ? filters.cities.join(',') : undefined,
    abc_classifications: filters.abcClassifications.length > 0 ? filters.abcClassifications.join(',') : undefined,
    start_date: filters.start_date || undefined,
    end_date: filters.end_date || undefined,
    limit: itemsPerPage,
    offset: (page - 1) * itemsPerPage,
  });

  const { data: inventory, isLoading } = useQuery<InventoryStatus[]>({
    queryKey: ['inventory', filters, statusFilter, page],
    queryFn: async () => {
      const response = await api.getInventoryStatus(getFilterParams());
      return response.data;
    },
  });

  // Get ALL items (unpaginated) for mix analysis aggregation
  const { data: allInventory } = useQuery<InventoryStatus[]>({
    queryKey: ['inventoryAll', filters, statusFilter],
    queryFn: async () => {
      const response = await api.getInventoryStatus({
        ...getFilterParams(),
        limit: 10000,
        offset: 0,
      });
      return response.data;
    },
  });

  const totalCount = allInventory?.length || 0;
  const totalPages = totalCount ? Math.ceil(totalCount / itemsPerPage) : 1;

  // Stock evolution charts (same as Dashboard)
  const { data: stockByLifecycle } = useQuery<{ data: any[]; groups: string[] }>({
    queryKey: ['inventory-stock-lifecycle', filters.brands],
    queryFn: () => api.getDashboardMonthlyStockByLifecycle({
      brands: filters.brands.length > 0 ? filters.brands : undefined,
    }),
    retry: 1,
  });

  const { data: stockByAbc } = useQuery<{ data: any[]; groups: string[] }>({
    queryKey: ['inventory-stock-abc', filters.brands],
    queryFn: () => api.getDashboardMonthlyStockByAbc({
      brands: filters.brands.length > 0 ? filters.brands : undefined,
    }),
    retry: 1,
  });

  // ─── Stock Mix Analysis (computed from all inventory data) ───
  const mixByCategory = useMemo(() => {
    if (!allInventory?.length) return [];
    const map: Record<string, { units: number; value: number }> = {};
    allInventory.forEach((item: any) => {
      const cat = item.category || 'Unknown';
      if (!map[cat]) map[cat] = { units: 0, value: 0 };
      map[cat].units += item.current_stock || 0;
      map[cat].value += item.cogs_value || 0;
    });
    return Object.entries(map)
      .map(([name, d]) => ({ name, units: d.units, value: d.value }))
      .sort((a, b) => b.value - a.value);
  }, [allInventory]);

  const mixByStatus = useMemo(() => {
    if (!allInventory?.length) return [];
    const map: Record<string, { count: number; units: number; value: number }> = {};
    allInventory.forEach((item: any) => {
      const s = item.status || 'Unknown';
      if (!map[s]) map[s] = { count: 0, units: 0, value: 0 };
      map[s].count += 1;
      map[s].units += item.current_stock || 0;
      map[s].value += item.cogs_value || 0;
    });
    const order = ['RED', 'YELLOW', 'GREEN'];
    return Object.entries(map)
      .map(([name, d]) => ({ name, ...d }))
      .sort((a, b) => order.indexOf(a.name) - order.indexOf(b.name));
  }, [allInventory]);

  const mixByAbc = useMemo(() => {
    if (!allInventory?.length) return [];
    const map: Record<string, { count: number; units: number; value: number }> = {};
    allInventory.forEach((item: any) => {
      const abc = (item.abc_classification || 'N/A').toUpperCase();
      if (!map[abc]) map[abc] = { count: 0, units: 0, value: 0 };
      map[abc].count += 1;
      map[abc].units += item.current_stock || 0;
      map[abc].value += item.cogs_value || 0;
    });
    const order = ['A', 'B', 'C', 'D', 'E', 'N/A'];
    return Object.entries(map)
      .map(([name, d]) => ({ name, ...d }))
      .sort((a, b) => order.indexOf(a.name) - order.indexOf(b.name));
  }, [allInventory]);

  // KPI summary
  const kpiSummary = useMemo(() => {
    if (!allInventory?.length) return { totalUnits: 0, totalValue: 0, totalSKUs: 0, redCount: 0, yellowCount: 0, greenCount: 0 };
    let totalUnits = 0, totalValue = 0, redCount = 0, yellowCount = 0, greenCount = 0;
    allInventory.forEach((item: any) => {
      totalUnits += item.current_stock || 0;
      totalValue += item.cogs_value || 0;
      if (item.status === 'RED') redCount++;
      else if (item.status === 'YELLOW') yellowCount++;
      else if (item.status === 'GREEN') greenCount++;
    });
    return { totalUnits, totalValue, totalSKUs: allInventory.length, redCount, yellowCount, greenCount };
  }, [allInventory]);

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'RED':
        return 'status-badge status-badge--danger';
      case 'YELLOW':
        return 'status-badge status-badge--warning';
      case 'GREEN':
        return 'status-badge status-badge--success';
      default:
        return 'status-badge status-badge--neutral';
    }
  };

  const getDaysOfSupplyColor = (days: number) => {
    if (days < 7) return 'var(--color-danger-bg)';
    if (days < 14) return 'var(--color-warning-bg)';
    if (days < 30) return '#FFF9C4';
    if (days < 60) return 'var(--color-success-bg)';
    return 'var(--color-success-bg)';
  };

  const getDaysOfSupplyTextColor = (days: number) => {
    if (days < 7) return 'var(--color-danger)';
    if (days < 14) return 'var(--color-warning)';
    if (days < 30) return '#F57F17';
    if (days < 60) return 'var(--color-success)';
    return 'var(--color-success)';
  };

  const getDaysOfSupplyLabel = (days: number) => {
    if (days < 7) return 'Critical';
    if (days < 14) return 'Low';
    if (days < 30) return 'Moderate';
    if (days < 60) return 'Good';
    return 'Excellent';
  };

  const getLifecycleBadgeClass = (stage: string) => {
    switch (stage) {
      case 'new_launch':
      case 'active':
        return 'status-badge status-badge--success';
      case 'mature':
        return 'status-badge status-badge--neutral';
      case 'phase_out':
        return 'status-badge status-badge--warning';
      default:
        return 'status-badge status-badge--danger';
    }
  };

  return (
    <FullscreenWrap>
    <Box sx={{ maxWidth: 'var(--content-max-width)' }}>
      {/* Universal Filters */}
      <UniversalFilters
        brands={filters.brands}
        stores={filters.stores}
        categories={filters.categories}
        cities={filters.cities}
        abcClassifications={filters.abcClassifications}
        onBrandsChange={(brands) => { setFilters({ ...filters, brands }); setPage(1); }}
        onStoresChange={(stores) => { setFilters({ ...filters, stores }); setPage(1); }}
        onCategoriesChange={(categories) => { setFilters({ ...filters, categories }); setPage(1); }}
        onCitiesChange={(cities) => { setFilters({ ...filters, cities }); setPage(1); }}
        onAbcClassificationsChange={(abcClassifications) => { setFilters({ ...filters, abcClassifications }); setPage(1); }}
        onClearAll={() => {
          setFilters({ brands: [], stores: [], categories: [], cities: [], abcClassifications: [], start_date: new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], end_date: new Date().toISOString().split('T')[0] });
          setStatusFilter('');
        }}
        showCountries={false}
        showRetailers={false}
        showBrands={true}
        showStores={true}
        showCategories={true}
        showCities={true}
        showAbcClassifications={true}
        customFilters={
          <>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                size="small"
                type="date"
                label="Start Date"
                value={filters.start_date}
                onChange={(e) => { setFilters({ ...filters, start_date: e.target.value }); setPage(1); }}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                size="small"
                type="date"
                label="End Date"
                value={filters.end_date}
                onChange={(e) => { setFilters({ ...filters, end_date: e.target.value }); setPage(1); }}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Stock Health</InputLabel>
                <Select
                  value={statusFilter}
                  label="Stock Health"
                  onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="RED">Critical (Red)</MenuItem>
                  <MenuItem value="YELLOW">Warning (Yellow)</MenuItem>
                  <MenuItem value="GREEN">Healthy (Green)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </>
        }
      />

      {/* ─── KPI Summary Cards ─── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={4} md={2}>
          <Paper sx={{ p: '14px 18px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', height: '100%' }}>
            <Typography sx={{ fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--color-text-muted)', mb: 0.5 }}>
              Total SKUs
            </Typography>
            <Typography sx={{ fontSize: 'var(--text-2xl)', fontWeight: 700, color: 'var(--color-text-primary)', fontFamily: 'var(--font-heading)', fontVariantNumeric: 'tabular-nums' }}>
              {fmtNumber(kpiSummary.totalSKUs)}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Paper sx={{ p: '14px 18px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', height: '100%' }}>
            <Typography sx={{ fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--color-text-muted)', mb: 0.5 }}>
              Total Units
            </Typography>
            <Typography sx={{ fontSize: 'var(--text-2xl)', fontWeight: 700, color: 'var(--color-text-primary)', fontFamily: 'var(--font-heading)', fontVariantNumeric: 'tabular-nums' }}>
              {fmtNumber(kpiSummary.totalUnits)}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Paper sx={{ p: '14px 18px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', height: '100%' }}>
            <Typography sx={{ fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--color-text-muted)', mb: 0.5 }}>
              COGS Value
            </Typography>
            <Typography sx={{ fontSize: 'var(--text-2xl)', fontWeight: 700, color: 'var(--color-text-primary)', fontFamily: 'var(--font-heading)', fontVariantNumeric: 'tabular-nums' }}>
              {fmtCurrency(kpiSummary.totalValue)}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Paper sx={{ p: '14px 18px', border: '1px solid #DC2626', borderRadius: 'var(--radius-md)', height: '100%' }}>
            <Typography sx={{ fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: '#DC2626', mb: 0.5 }}>
              Critical (Red)
            </Typography>
            <Typography sx={{ fontSize: 'var(--text-2xl)', fontWeight: 700, color: '#DC2626', fontFamily: 'var(--font-heading)', fontVariantNumeric: 'tabular-nums' }}>
              {fmtNumber(kpiSummary.redCount)}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Paper sx={{ p: '14px 18px', border: '1px solid #F59E0B', borderRadius: 'var(--radius-md)', height: '100%' }}>
            <Typography sx={{ fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: '#F59E0B', mb: 0.5 }}>
              Warning (Yellow)
            </Typography>
            <Typography sx={{ fontSize: 'var(--text-2xl)', fontWeight: 700, color: '#F59E0B', fontFamily: 'var(--font-heading)', fontVariantNumeric: 'tabular-nums' }}>
              {fmtNumber(kpiSummary.yellowCount)}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Paper sx={{ p: '14px 18px', border: '1px solid #16A34A', borderRadius: 'var(--radius-md)', height: '100%' }}>
            <Typography sx={{ fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: '#16A34A', mb: 0.5 }}>
              Healthy (Green)
            </Typography>
            <Typography sx={{ fontSize: 'var(--text-2xl)', fontWeight: 700, color: '#16A34A', fontFamily: 'var(--font-heading)', fontVariantNumeric: 'tabular-nums' }}>
              {fmtNumber(kpiSummary.greenCount)}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* ─── Export ─── */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 1 }}>
        <ExportMenu
          data={(allInventory || []).map((item: any) => ({
            SKU: item.sku, Product: item.product_name, Category: item.category,
            Brand: item.brand, Store: item.store_name, Retailer: item.retailer_name,
            Country: item.country, 'Stock Health': item.status, 'Item Status': item.lifecycle_stage,
            'Current Stock': item.current_stock, 'In Transit': item.in_transit,
            'Daily Sales': Math.ceil(Number(item.avg_daily_sales) || 0), 'Days Supply': item.days_of_supply,
            'COGS Value': item.cogs_value,
          }))}
          filename={`Inventory_${new Date().toISOString().slice(0, 10)}`}
          title="Inventory Level"
          chartRef={chartContainerRef}
        />
        <FullscreenButton isFullscreen={isFullscreen} onToggle={toggleFullscreen} />
      </Box>

      {/* ─── Charts Section Toggle ──�� */}
      <Box
        onClick={() => setChartsOpen(!chartsOpen)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          cursor: 'pointer',
          mb: 2,
          p: '10px 16px',
          bgcolor: 'var(--color-surface)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-md)',
          '&:hover': { bgcolor: 'var(--color-bg)' },
        }}
      >
        <IconButton size="small" sx={{ p: 0 }}>
          {chartsOpen ? <ExpandMore sx={{ fontSize: 20 }} /> : <ChevronRight sx={{ fontSize: 20 }} />}
        </IconButton>
        <Typography sx={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--color-text-primary)' }}>
          Stock Analysis Charts
        </Typography>
        <Typography sx={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', ml: 1 }}>
          {chartsOpen ? 'Click to hide' : 'Click to show'}
        </Typography>
      </Box>

      <Collapse in={chartsOpen}>
        <div ref={chartContainerRef}>
        {/* ─── Row 1: Stock Evolution Charts ─── */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {/* Stock Evolution by Lifecycle */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: '20px', border: '1px solid var(--color-border)', height: 420 }}>
              <Typography sx={{ fontSize: 'var(--text-sm)', fontWeight: 600, mb: 0.5 }}>Stock Evolution by Lifecycle</Typography>
              <Typography sx={{ fontSize: '10px', color: 'var(--color-text-muted)', mb: 2 }}>
                Monthly stock quantity — actual &amp; projected
              </Typography>
              {(stockByLifecycle?.data?.length ?? 0) > 0 ? (
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart data={stockByLifecycle!.data} margin={{ left: 8, right: 8, top: 10 }} barCategoryGap="18%">
                    <CartesianGrid strokeDasharray="3 3" stroke="#EFEFEA" vertical={false} />
                    <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#8A8A85' }} tickLine={false} axisLine={{ stroke: '#E5E5E0' }} />
                    <YAxis tick={{ fontSize: 10, fill: '#8A8A85' }} tickLine={false} axisLine={false} tickFormatter={(v: number) => v >= 1000000 ? `${(v / 1000000).toFixed(1)}M` : v >= 1000 ? `${(v / 1000).toFixed(0)}K` : `${v}`} />
                    <RTooltip
                      formatter={(value: number, name: string) => [fmtNumber(value) + ' units', name.replace('_qty', '')]}
                      contentStyle={chartTooltipStyle}
                    />
                    <Legend wrapperStyle={{ fontSize: '10px' }} formatter={(val: string) => val.replace('_qty', '')} />
                    {(stockByLifecycle?.groups || []).map((grp: string, idx: number) => (
                      <Bar key={grp} dataKey={`${grp}_qty`} stackId="lifecycle" name={grp}
                        fill={LIFECYCLE_COLORS[grp] || CATEGORY_COLORS[idx % CATEGORY_COLORS.length]}
                        radius={idx === (stockByLifecycle?.groups || []).length - 1 ? [3, 3, 0, 0] : [0, 0, 0, 0]}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography sx={{ color: 'var(--color-text-muted)' }}>No lifecycle stock data</Typography>
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Stock Evolution by ABC */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: '20px', border: '1px solid var(--color-border)', height: 420 }}>
              <Typography sx={{ fontSize: 'var(--text-sm)', fontWeight: 600, mb: 0.5 }}>Stock Evolution by ABC Classification</Typography>
              <Typography sx={{ fontSize: '10px', color: 'var(--color-text-muted)', mb: 2 }}>
                Monthly stock value — actual &amp; projected
              </Typography>
              {(stockByAbc?.data?.length ?? 0) > 0 ? (
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart data={stockByAbc!.data} margin={{ left: 8, right: 8, top: 10 }} barCategoryGap="18%">
                    <CartesianGrid strokeDasharray="3 3" stroke="#EFEFEA" vertical={false} />
                    <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#8A8A85' }} tickLine={false} axisLine={{ stroke: '#E5E5E0' }} />
                    <YAxis tick={{ fontSize: 10, fill: '#8A8A85' }} tickLine={false} axisLine={false} tickFormatter={(v: number) => v >= 1000000 ? `${(v / 1000000).toFixed(1)}M` : v >= 1000 ? `${(v / 1000).toFixed(0)}K` : `${v}`} />
                    <RTooltip
                      formatter={(value: number, name: string) => [fmtCurrency(value), 'Class ' + name.replace('_val', '')]}
                      contentStyle={chartTooltipStyle}
                    />
                    <Legend wrapperStyle={{ fontSize: '10px' }} formatter={(val: string) => 'Class ' + val.replace('_val', '')} />
                    {(stockByAbc?.groups || []).map((grp: string, idx: number) => (
                      <Bar key={grp} dataKey={`${grp}_val`} stackId="abc" name={grp}
                        fill={ABC_CHART_COLORS[grp] || CATEGORY_COLORS[idx % CATEGORY_COLORS.length]}
                        radius={idx === (stockByAbc?.groups || []).length - 1 ? [3, 3, 0, 0] : [0, 0, 0, 0]}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography sx={{ color: 'var(--color-text-muted)' }}>No ABC stock data</Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>

        {/* ─── Row 2: Stock Mix Analysis ─── */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {/* Mix by Category */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: '20px', border: '1px solid var(--color-border)', height: 400 }}>
              <Typography sx={{ fontSize: 'var(--text-sm)', fontWeight: 600, mb: 0.5 }}>Stock Mix by Category</Typography>
              <Typography sx={{ fontSize: '10px', color: 'var(--color-text-muted)', mb: 2 }}>
                COGS value distribution across categories
              </Typography>
              {mixByCategory.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={mixByCategory}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      label={renderPieLabel}
                      labelLine={{ stroke: '#8A8A85', strokeWidth: 0.5 }}
                    >
                      {mixByCategory.map((_, idx) => (
                        <Cell key={idx} fill={CATEGORY_COLORS[idx % CATEGORY_COLORS.length]} />
                      ))}
                    </Pie>
                    <RTooltip
                      formatter={(value: number, name: string) => [fmtCurrency(value), name]}
                      contentStyle={chartTooltipStyle}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography sx={{ color: 'var(--color-text-muted)' }}>No data</Typography>
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Mix by Item Status */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: '20px', border: '1px solid var(--color-border)', height: 400 }}>
              <Typography sx={{ fontSize: 'var(--text-sm)', fontWeight: 600, mb: 0.5 }}>Stock Mix by Stock Health</Typography>
              <Typography sx={{ fontSize: '10px', color: 'var(--color-text-muted)', mb: 2 }}>
                SKU count &amp; COGS value by health status
              </Typography>
              {mixByStatus.length > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie
                        data={mixByStatus}
                        dataKey="count"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={70}
                        innerRadius={35}
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        labelLine={{ stroke: '#8A8A85', strokeWidth: 0.5 }}
                      >
                        {mixByStatus.map((entry) => (
                          <Cell key={entry.name} fill={STATUS_COLORS[entry.name] || '#9CA3AF'} />
                        ))}
                      </Pie>
                      <RTooltip
                        formatter={(value: number, name: string) => [fmtNumber(value) + ' SKUs', name]}
                        contentStyle={chartTooltipStyle}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  {/* Status legend table */}
                  <Box sx={{ mt: 1 }}>
                    {mixByStatus.map((s) => (
                      <Box key={s.name} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5, borderBottom: '1px solid var(--color-border)' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: STATUS_COLORS[s.name] || '#9CA3AF' }} />
                          <Typography sx={{ fontSize: 'var(--text-xs)', fontWeight: 600 }}>{s.name}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                          <Typography sx={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>{fmtNumber(s.count)} SKUs</Typography>
                          <Typography sx={{ fontSize: 'var(--text-xs)', fontWeight: 500 }}>{fmtCurrency(s.value)}</Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </>
              ) : (
                <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography sx={{ color: 'var(--color-text-muted)' }}>No data</Typography>
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Mix by ABC Class */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: '20px', border: '1px solid var(--color-border)', height: 400 }}>
              <Typography sx={{ fontSize: 'var(--text-sm)', fontWeight: 600, mb: 0.5 }}>Stock Mix by ABC Class</Typography>
              <Typography sx={{ fontSize: '10px', color: 'var(--color-text-muted)', mb: 2 }}>
                SKU count &amp; COGS value by ABC classification
              </Typography>
              {mixByAbc.length > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={mixByAbc} margin={{ left: 0, right: 0, top: 5 }} barCategoryGap="25%">
                      <CartesianGrid strokeDasharray="3 3" stroke="#EFEFEA" vertical={false} />
                      <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#8A8A85' }} tickLine={false} axisLine={{ stroke: '#E5E5E0' }} />
                      <YAxis tick={{ fontSize: 10, fill: '#8A8A85' }} tickLine={false} axisLine={false} tickFormatter={(v: number) => v >= 1000000 ? `${(v / 1000000).toFixed(1)}M` : v >= 1000 ? `${(v / 1000).toFixed(0)}K` : `${v}`} />
                      <RTooltip
                        formatter={(value: number, name: string) => [name === 'value' ? fmtCurrency(value) : fmtNumber(value), name === 'value' ? 'COGS Value' : 'Units']}
                        contentStyle={chartTooltipStyle}
                      />
                      <Bar dataKey="value" name="value" radius={[3, 3, 0, 0]}>
                        {mixByAbc.map((entry) => (
                          <Cell key={entry.name} fill={ABC_CHART_COLORS[entry.name] || '#9CA3AF'} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                  {/* ABC legend table */}
                  <Box sx={{ mt: 1 }}>
                    {mixByAbc.map((a) => (
                      <Box key={a.name} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5, borderBottom: '1px solid var(--color-border)' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip label={a.name} size="small" sx={{ bgcolor: ABC_CHART_COLORS[a.name] || '#9CA3AF', color: 'white', fontWeight: 700, width: 32, fontSize: '10px', height: 20 }} />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                          <Typography sx={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>{fmtNumber(a.count)} SKUs</Typography>
                          <Typography sx={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>{fmtNumber(a.units)} units</Typography>
                          <Typography sx={{ fontSize: 'var(--text-xs)', fontWeight: 500 }}>{fmtCurrency(a.value)}</Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </>
              ) : (
                <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography sx={{ color: 'var(--color-text-muted)' }}>No data</Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      </div>
      </Collapse>

      {/* Enhanced Inventory Table */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>SKU</TableCell>
              <TableCell>EAN Code</TableCell>
              <TableCell>Product</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Sub-category</TableCell>
              <TableCell>ABC</TableCell>
              <TableCell>Store</TableCell>
              <TableCell>Retailer</TableCell>
              <TableCell>Mall</TableCell>
              <TableCell>Country</TableCell>
              <TableCell align="right">Stock</TableCell>
              <TableCell align="right">In Transit</TableCell>
              <TableCell align="right">Daily Sales</TableCell>
              <TableCell align="right">Days Supply</TableCell>
              <TableCell align="right">COGS Value</TableCell>
              <TableCell>Stock Health</TableCell>
              <TableCell>Item Status</TableCell>
              <TableCell>Stockout</TableCell>
              <TableCell>Overstock</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={19} align="center" sx={{ py: 4 }}>
                  Loading...
                </TableCell>
              </TableRow>
            ) : inventory && inventory.length > 0 ? (
              inventory.map((item, index) => (
                <TableRow key={index} hover>
                  <TableCell>
                    <Typography
                      sx={{
                        fontSize: 'var(--text-sm)',
                        fontFamily: 'var(--font-mono)',
                        color: 'var(--color-text-secondary)',
                      }}
                    >
                      {item.sku}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      sx={{
                        fontSize: 'var(--text-xs)',
                        fontFamily: 'var(--font-mono)',
                        color: 'var(--color-text-muted)',
                      }}
                    >
                      {(item as any).ean_code || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-primary)' }}>
                      {item.product_name}
                    </Typography>
                    {(item as any).brand && (
                      <Typography
                        sx={{
                          fontSize: 'var(--text-xs)',
                          color: 'var(--color-text-muted)',
                          display: 'block',
                        }}
                      >
                        {(item as any).brand}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ fontSize: 'var(--text-sm)' }}>
                      {(item as any).category || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                      {(item as any).subcategory || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {(item as any).abc_classification ? (
                      <MuiTooltip title={ABC_LABELS[(item as any).abc_classification.toLowerCase()]}>
                        <Chip
                          label={(item as any).abc_classification.toUpperCase()}
                          size="small"
                          variant="filled"
                          sx={{
                            bgcolor: ABC_COLORS[(item as any).abc_classification.toLowerCase()],
                            color: ABC_TEXT_COLORS[(item as any).abc_classification.toLowerCase()] || '#616161',
                            fontWeight: 700,
                            fontSize: '11px',
                            height: 20,
                          }}
                        />
                      </MuiTooltip>
                    ) : (
                      <Typography sx={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>-</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ fontSize: 'var(--text-sm)' }}>{item.store_name}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ fontSize: 'var(--text-sm)' }}>{(item as any).retailer_name || 'N/A'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                      {(item as any).mall_name || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ fontSize: 'var(--text-sm)' }}>
                      {(item as any).country || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography sx={{ fontSize: 'var(--text-sm)', fontWeight: 500 }}>
                      {item.current_stock.toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography sx={{ fontSize: 'var(--text-sm)' }}>
                      {item.in_transit.toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography sx={{ fontSize: 'var(--text-sm)' }}>
                      {Math.ceil(Number(item.avg_daily_sales) || 0)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <MuiTooltip title={getDaysOfSupplyLabel(item.days_of_supply)}>
                      <Box sx={{
                        bgcolor: getDaysOfSupplyColor(item.days_of_supply),
                        p: 1,
                        borderRadius: 'var(--radius-sm)',
                        textAlign: 'center',
                        minWidth: 60,
                        cursor: 'help'
                      }}>
                        <Typography
                          sx={{
                            fontSize: 'var(--text-sm)',
                            fontWeight: 700,
                            color: getDaysOfSupplyTextColor(item.days_of_supply),
                            fontVariantNumeric: 'tabular-nums',
                          }}
                        >
                          {item.days_of_supply.toFixed(1)}
                        </Typography>
                      </Box>
                    </MuiTooltip>
                  </TableCell>
                  <TableCell align="right">
                    <Typography sx={{ fontSize: 'var(--text-sm)', fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>
                      {fmtCurrency((item as any).cogs_value || 0)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <span className={getStatusBadgeClass(item.status)}>
                      {item.status}
                    </span>
                  </TableCell>
                  <TableCell>
                    {(item as any).lifecycle_stage ? (
                      <span className={getLifecycleBadgeClass((item as any).lifecycle_stage)}>
                        {(item as any).lifecycle_stage.replace('_', ' ').toUpperCase()}
                      </span>
                    ) : (
                      <Typography sx={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>-</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {(item as any).stockout_duration ? (
                      <MuiTooltip title={`Out of stock for ${(item as any).stockout_duration} days`}>
                        <span className="status-badge status-badge--danger">
                          {(item as any).stockout_duration}d
                        </span>
                      </MuiTooltip>
                    ) : (
                      <Typography sx={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>-</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {(item as any).overstock_quantity > 0 ? (
                      <MuiTooltip title={`Overstock: ${(item as any).overstock_quantity} units | Value: ${fmtCurrency((item as any).overstock_value || 0)}`}>
                        <span className="status-badge status-badge--warning">
                          Overstock
                        </span>
                      </MuiTooltip>
                    ) : (
                      <Typography sx={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>-</Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={19} align="center" sx={{ py: 4 }}>
                  <Typography sx={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
                    No inventory data found matching the current filters
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
        <Pagination
          count={totalPages}
          page={page}
          onChange={handlePageChange}
          color="primary"
          showFirstButton
          showLastButton
        />
      </Box>

      {/* Summary */}
      <Box sx={{ mt: 2, textAlign: 'center' }}>
        <Typography sx={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
          Showing {inventory?.length || 0} items (Page {page} of {totalPages})
          {totalCount > 0 && ` - Total: ${totalCount} items`}
        </Typography>
      </Box>
    </Box>
    </FullscreenWrap>
  );
};

export default InventoryList;
